import React, { useState, useEffect, Fragment } from 'react';

import request from '@nineeng/request';

import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import WelcomeScreen from '../layout/WelcomeScreen.jsx';
import ContentContainer from '../layout/ContentContainer.jsx';
import MultipleFileUpload from '../components/MultipleFileUpload.jsx';
import VATField from '../components/VATField.jsx';
import SingleFileUpload from '../components/SingleFileUpload.jsx';

import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Box from '@mui/material/Box';
import WarningIcon from '@mui/icons-material/Warning';

import {
	getSatisfiedDocumentTypes,
	getTypesAcceptableForRequirements,
	flattenRequirementTypes,
	isRequirementGroupSatisfied,
	formatRequirementGroup,
	getUnsatisfiedRequirements,
} from '../util/safetyCertificates.js';

import { format } from 'date-fns';
import { getTextDefaults } from './translations/safetyCertificatesTranslations';

const CERTIFICATE_DISPLAY_NAMES = {
	'vca-company-p': 'VCA P',
	'vca-company-**': 'VCA **',
	'vca-company-*': 'VCA *',
	'vca-company-u': 'VCA U',
	'ssc-company-p': 'SSC P',
	'ssc-company-**': 'SSC **',
	'ssc-company-*': 'SSC *',
	'ssc-company-u': 'SSC U',
	'mase': 'MASE',
	'iso-45001': 'ISO 45001',
	'iso-certificate': 'ISO Certificate'
};

// Page Components
const WelcomePage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Typography variant='h6' sx={{ marginBottom: 2 }}>{text.title}</Typography>
			<Typography variant='body1'>{text.intro}</Typography>
		</WelcomeScreen>
	);
};

const CompanyVatPage = ({ input, text, onInput, disabled }) => {
	return (
		<ContentContainer>
			<VATField
				label={text.lblVAT}
				onChange={onInput}
				name='input.vatNumber'
				value={input.vatNumber || ''}
				disabled={disabled}
			/>
		</ContentContainer>
	);
};

const ProcessingPage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Box sx={{ textAlign: 'center' }}>
				<CircularProgress sx={{ marginBottom: 2 }} />
				<Typography>{text.processingText}</Typography>
			</Box>
		</WelcomeScreen>
	);
};

const getDisplayName = (type) => {
	return CERTIFICATE_DISPLAY_NAMES[type] || type;
};

// Helper function to convert array of requirement groups to formatted display string
// Supports both flat arrays (backwards compatible) and nested arrays (OR relationships)
// Examples:
// - ['vca-company-p', 'iso45001'] → 'VCA P, ISO 45001'
// - ['vca-company-p', ['iso45001', 'mase']] → 'VCA P, ISO 45001 or MASE'
const formatTypesList = (requirementGroups, separator) => {
	return requirementGroups
		.map(group => formatRequirementGroup(group, getDisplayName, separator))
		.join(', ');
};

const ConfirmationPage = ({
	text,
	groupCertificates = [],
	groupDocuments = [],
	onCertificateToggle,
	onDocumentChange,
	onNoDocumentsToggle,
	noDocumentsConfirmed,
	disabled,
	uploadUrl,
	input,
	useCamera,
	cameraIncludes,
	cameraRotationAngle,
	possibleDocumentTypes,
	requiredDocumentTypes,
	gapAnalysisEnabled,
	gapAnalysisConfirmed,
	gapAnalysisDocuments,
	// onGapAnalysisToggle,
	onGapAnalysisDocumentChange,
	defaultGapAnalysisId,
	onDocumentOptionChange,
	documentOption,
	showTypesInSubtitle,
}) => {
	const hasCertificates = groupCertificates.length > 0;
	const hasSelectedCertificates = groupCertificates.some(cert => cert.selected !== false);
	const hasDocuments = groupDocuments.length > 0;
	const hasAnyContent = hasSelectedCertificates || hasDocuments;
	const showNoDocuments = requiredDocumentTypes.length === 0 && (!hasAnyContent || (hasCertificates && !hasSelectedCertificates));

	const satisfiedTypes = getSatisfiedDocumentTypes('group', groupCertificates, groupDocuments);

	// Split allowed types into still-needed required and optional
	// Uses utility function that handles both flat arrays and nested arrays (OR relationships)
	const stillNeededRequired = getUnsatisfiedRequirements(requiredDocumentTypes, satisfiedTypes);
	// const optionalTypes = possibleDocumentTypes.filter(type =>
	// 	!requiredDocumentTypes.includes(type) && !satisfiedTypes.includes(type)
	// );
	const companyInformation = hasCertificates ? groupCertificates.find(cert => cert.extra?.company).extra.company : undefined;

	return (
		<ContentContainer>
			<Typography variant='h5' sx={{ marginBottom: 3 }}>{text.confirmationTitle}</Typography>

			{/* Found Certificates Section */}
			{hasCertificates && (
				<Box sx={{ marginBottom: 2 }}>
					<Typography variant='h6' sx={{ marginBottom: 1 }}>{text.foundCertificatesTitle}</Typography>
					<Typography variant='body2' sx={{ marginBottom: 2, color: 'text.secondary' }}>
						{text.foundCertificatesSubtitle}
					</Typography>
					{groupCertificates.map((cert, index) => (
						<Card key={cert.certificateNumber || index} variant="outlined" sx={{ '& p': { margin: 0 }, marginBottom: 2, width: '100%' }}>
							<ListItem
								sx={{ height: 100 }}
								disabled={disabled}
							>
								<ListItemIcon sx={{ minWidth: 80 }}>
									<Checkbox
										checked={cert.selected !== false}
										onChange={(e) => onCertificateToggle(index, e.target.checked)}
										disabled={disabled}
									/>
								</ListItemIcon>
								<ListItemText
									primary={cert.certificateType}
									primaryTypographyProps={{ color: 'green', noWrap: true }}
									secondary={<Fragment>
										<Typography>{text.certificateNumber}: {cert.certificateNumber}</Typography>
										<Typography variant='caption'>{text.certificateIssuer}: {cert.certificateIssuer}</Typography>
										{cert.validUntil && (
											<Typography variant='caption' sx={{ marginLeft: 1 }}>
												{text.validUntil}: {format(new Date(cert.validUntil), 'dd/MM/yyyy')}
											</Typography>
										)}
									</Fragment>}
								/>
							</ListItem>
						</Card>
					))}
				</Box>
			)}

			{/* No Certificates Found Message */}
			{!hasCertificates && (
				<Box sx={{ marginBottom: 2 }}>
					<Typography variant='h6' sx={{ marginBottom: 1 }}>{text.noCertificatesFoundTitle}</Typography>
					<Typography variant='body2' sx={{ marginBottom: 2, color: 'text.secondary' }}>
						{text.noCertificatesFoundMessage}
					</Typography>
				</Box>
			)}

			<Box sx={{ marginBottom: 4, width: '100%' }}>
				<Typography variant='h6' sx={{ marginBottom: 1 }}>{text.uploadDocumentsTitle}</Typography>
			<Typography variant='body2' sx={{ marginBottom: 1 }}>
				{stillNeededRequired.length > 0
					? showTypesInSubtitle
						? text.confirmationRequiredSubtitle + formatTypesList(stillNeededRequired, text.orSeparator)
						: text.confirmationRequiredSubtitle
					: text.confirmationRequiredSatisfied
				}
			</Typography>
			{possibleDocumentTypes.length > 0 && (
				<Typography variant='body2' sx={{ marginBottom: 2, color: 'text.secondary' }}>
					{showTypesInSubtitle
						? text.confirmationOptionalSubtitle + formatTypesList(possibleDocumentTypes, text.orSeparator)
						: text.confirmationOptionalSubtitle}
				</Typography>
			)}

				<MultipleFileUpload
					onFilesUploaded={onDocumentChange}
					uploadUrl={uploadUrl}
					disabled={disabled}
					text={text}
					existingFiles={groupDocuments}
					fileType="unknown-group-document-type"
					allowedDocumentTypes={getTypesAcceptableForRequirements('group', [
						...flattenRequirementTypes(stillNeededRequired),
						...flattenRequirementTypes(possibleDocumentTypes),
					])}
					context={{
						expectedFile: 'safety or training related certificate of a company (not a person). Can be VCA, ISO-45001, MASE or other certificates',
						companyData: { ...companyInformation, ...input },
					}}
					possibleDocumentTypes={possibleDocumentTypes}
					requiredDocumentTypes={requiredDocumentTypes}
					getSatisfiedTypes={(certType) => getSatisfiedDocumentTypes('group', [{ type: certType, selected: true }], [])}
					useCamera={useCamera}
					cameraIncludes={cameraIncludes}
					cameraRotationAngle={cameraRotationAngle}
				/>
			</Box>


			{/* Document options section */}
			{gapAnalysisEnabled && (
				<Box sx={{ marginBottom: 2 }}>
					{/* GAP Analysis option - always shown when enabled, independent of uploaded documents */}
					{gapAnalysisEnabled && (
						<Fragment>
							<FormControlLabel
								control={
									<Checkbox
										checked={gapAnalysisConfirmed}
										onChange={(e) => onDocumentOptionChange({
											target: { value: e.target.checked ? 'gapAnalysis' : '' }
										})}
										disabled={disabled}
									/>
								}
								label={
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
										<WarningIcon color="warning" />
										<Typography variant='body2'>{text.gapAnalysisCheckbox}</Typography>
									</Box>
								}
							/>

							{/* GAP Analysis Upload Section */}
							{gapAnalysisConfirmed && (
								<Box sx={{ marginTop: 2, marginLeft: 4 }}>
									<Typography variant='h6' sx={{ marginBottom: 1 }}>{text.gapAnalysisUploadTitle}</Typography>
									{defaultGapAnalysisId && (
										<Typography
											variant='body2'
											sx={{
												marginBottom: 2,
												color: 'text.secondary',
												textDecoration: 'underline',
												cursor: 'pointer',
											}}
											component='a'
											href={`${uploadUrl}/${defaultGapAnalysisId}`}
											download
										>
											{text.gapAnalysisTemplateLink}
										</Typography>
									)}
									<SingleFileUpload
										onFileUploaded={onGapAnalysisDocumentChange}
										uploadUrl={uploadUrl}
										disabled={disabled}
										text={text}
										existingFile={gapAnalysisDocuments[0] || null}
										fileType="gap-analysis"
										accept={{
											'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
										}}
										uploadZone='gap-analysis'
									/>
								</Box>
							)}
						</Fragment>
					)}

					{/* No Documents option - only when no required types and no content provided */}
					{showNoDocuments && (
						<FormControlLabel
							sx={gapAnalysisEnabled ? { marginTop: 2 } : undefined}
							control={
								<Checkbox
									checked={noDocumentsConfirmed}
									onChange={(e) => gapAnalysisEnabled
										? onDocumentOptionChange({ target: { value: e.target.checked ? 'noDocuments' : '' } })
										: onNoDocumentsToggle(e.target.checked)}
									disabled={disabled}
								/>
							}
							label={
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<WarningIcon color="warning" />
									<Typography variant='body2'>{text.noDocumentsCheckbox}</Typography>
								</Box>
							}
						/>
					)}
				</Box>
			)}
		</ContentContainer>
	);
};

const ThankYouPage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Typography variant='h6' sx={{ marginBottom: 2 }}>{text.thankYouTitle}</Typography>
			<Typography variant='body1'>{text.thankYouText}</Typography>
		</WelcomeScreen>
	);
};

// Main Component
const SafetyCertificatesCompanySection = ({
	onChange = () => { },
	values = {},
	textOverride = {},
	background = 'light',
	navigationProps,
	selectedLanguage = 'EN',
	apiUrl = '/api/validate/vca',
	uploadUrl = '/api/files',
	disabled = false,
	useCamera = true,
	cameraIncludes = '',
	cameraRotationAngle = 0,
	possibleDocumentTypes = [],
	requiredDocumentTypes = [],
	gapAnalysisEnabled = false,
	defaultGapAnalysisId,
	noWelcome = false,
	showTypesInSubtitle = true,
	...rest
}) => {
	const text = Object.assign({}, getTextDefaults(selectedLanguage), textOverride);

	const inputData = values.input || {};
	const companyData = values.companyData || {};
	const input = {
		...companyData,
		...inputData
	}

	const groupCertificates = values.groupCertificates || [];
	const groupDocuments = values.groupDocuments || [];
	const gapAnalysisDocuments = values.gapAnalysisDocuments || [];
	const documentOption = values.documentOption || '';
	const gapAnalysisConfirmed = documentOption === 'gapAnalysis';
	const noDocumentsConfirmed = documentOption === 'noDocuments';

	// Check if user details are needed
	const needsCompanyVat = !input.vatNumber;
	const defaultPage = noWelcome ? (needsCompanyVat ? 'companyVat' : 'processing') : 'welcome';

	const handleCanContinue = () => {
		// Block if any uploads are invalid (AI rejected them)
		const hasInvalidUploads = groupDocuments.some(doc => doc.validity === false);
		if (hasInvalidUploads) return false;

		// Check if gap analysis option is confirmed and has documents
		if (gapAnalysisEnabled && gapAnalysisConfirmed) {
			return gapAnalysisDocuments.length > 0;
		}

		// Check if we have any valid content
		const hasSelectedCertificates = groupCertificates.some(c => c.selected !== false);
		const hasValidUploads = groupDocuments.some(doc => doc.validity !== false);
		const hasAnyContent = hasSelectedCertificates || hasValidUploads;

		// If no required types, just need content or explicit confirmation
		if (requiredDocumentTypes.length === 0) {
			return hasAnyContent || noDocumentsConfirmed;
		}

		// Check if all required types are satisfied
		// getSatisfiedDocumentTypes already combines certificates + valid documents with hierarchy
		// isRequirementGroupSatisfied handles both flat arrays and nested arrays (OR relationships)
		const satisfiedTypes = getSatisfiedDocumentTypes('group', groupCertificates, groupDocuments);
		const allRequiredSatisfied = requiredDocumentTypes.every(requirementGroup =>
			isRequirementGroupSatisfied(requirementGroup, satisfiedTypes),
		);

		return allRequiredSatisfied;
	};

	const screens = {
		welcome: {
			next: () => needsCompanyVat ? setCurrentScreen('companyVat') : handleBackgroundCheck(),
			component: WelcomePage,
			canContinue: true,
		},
		companyVat: {
			previous: (event) => {
				if (noWelcome) return navigationProps.previous(event);
				return setCurrentScreen('welcome')
			},
			next: () => handleBackgroundCheck(),
			component: CompanyVatPage,
			canContinue: !!(input.vatNumber),
		},
		processing: {
			component: ProcessingPage,
		},
		confirmation: {
			previous: (event) => {
				if (noWelcome) return navigationProps.previous(event);
				return setCurrentScreen('welcome')
			},
			next: () => setCurrentScreen('thankYou'),
			component: ConfirmationPage,
			canContinue: handleCanContinue(),
		},
		thankYou: {
			previous: () => setCurrentScreen('confirmation'),
			next: (event) => navigationProps.next(event),
			component: ThankYouPage,
			canContinue: true,
		},
	};

	const handleBackgroundCheck = async () => {
		try {
			setCurrentScreen('processing');

			const url = `${apiUrl}/company/${input.vatNumber}`;
			const { body } = await request.get(url);
			if (!body || !body.length) throw new Error('No certs');
			const certs = body;

			const existingCertNumbers = new Set(groupCertificates.map(c => c.certificateNumber));
			const certificatesWithSelection = certs.map(cert => ({
				...cert,
				selected: existingCertNumbers.size === 0 || existingCertNumbers.has(cert.certificateNumber),
			}));

			onChange({
				target: {
					name: 'groupCertificates',
					value: certificatesWithSelection,
				},
			});

			// Navigate to confirmation page (which now shows both certificates and upload)
			setCurrentScreen('confirmation');
		} catch (error) {
			console.error('Error searching for certificates:', error);
			// Even if no certificates found, go to confirmation page
			onChange({
				target: {
					name: 'groupCertificates',
					value: [],
				},
			});
			setCurrentScreen('confirmation');
		}
	};

	const handleCertificateToggle = (index, selected) => {
		const newCertificates = [...groupCertificates];
		newCertificates[index] = { ...newCertificates[index], selected };
		// const updatedCompanySafety = {
		// 	...companySafety,
		// 	groupCertificates: newCertificates,
		// };

		// if (selected) {
		// 	updatedCompanySafety.documentOption = '';
		// 	updatedCompanySafety.gapAnalysisDocuments = [];
		// }

		// // Remove uploaded documents that match the selected certificate's type
		// // This includes equivalent types and types satisfied by hierarchy
		// const selectedCertType = newCertificates[index].type;
		// const satisfiedTypes = getSatisfiedDocumentTypes('group', [{ type: selectedCertType, selected: true }], []);

		// // Filter out documents whose type would be satisfied by the selected certificate
		// updatedCompanySafety.groupDocuments = groupDocuments.filter(doc =>
		// 	!satisfiedTypes.includes(doc.type)
		// );

		onChange({
			target: {
				name: 'groupCertificates',
				value: newCertificates,
			},
		});
	};

	const handleDocumentChange = (newFiles) => {
		// const updatedCompanySafety = {
		// 	...companySafety,
		// 	groupDocuments: newFiles,
		// 	documentOption: '',
		// 	gapAnalysisDocuments: [],
		// };

		onChange({
			target: {
				name: 'groupDocuments',
				value: newFiles,
			},
		});
	};

	const handleNoDocumentsCheckbox = (confirmed) => {
		let value = null;
		if (confirmed) {
			value = 'noDocuments';
		}
		onChange({
			target: {
				name: 'documentOption',
				value: value,
			},
		});
	};

	const handleDocumentOptionChange = (e) => {
		onChange({
			target: {
				name: 'documentOption',
				value: e.target.value,
			},
		});
	};

	const handleGapAnalysisDocumentChange = (newFiles) => {
		onChange({
			target: {
				name: 'gapAnalysisDocuments',
				value: newFiles ? [newFiles] : [],
			},
		});
	};

	const handleInput = ({ target }) => onChange({
		target: {
			...target,
			name: target.name
		}
	});

	const [currentScreen, setCurrentScreen] = useState(defaultPage);
	const screen = screens[currentScreen] || screens.welcome;

	useEffect(() => {
		// If we should skip welcome and we have VAT number, trigger background check
		if (noWelcome && !needsCompanyVat && currentScreen === 'processing') {
			handleBackgroundCheck();
		}
	}, [noWelcome, needsCompanyVat]);

	const screenProps = {
		input,
		groupCertificates,
		groupDocuments,
		noDocumentsConfirmed,
		onInput: handleInput,
		onCertificateToggle: handleCertificateToggle,
		onDocumentChange: handleDocumentChange,
		onNoDocumentsToggle: handleNoDocumentsCheckbox,
		text,
		uploadUrl,
		disabled,
		useCamera,
		cameraIncludes,
		cameraRotationAngle,
		possibleDocumentTypes,
		requiredDocumentTypes,
		gapAnalysisEnabled,
		gapAnalysisConfirmed,
		gapAnalysisDocuments,
		onGapAnalysisDocumentChange: handleGapAnalysisDocumentChange,
		onDocumentOptionChange: handleDocumentOptionChange,
		documentOption,
		defaultGapAnalysisId,
		disableCompanyVat: !!companyData.vatNumber,
		showTypesInSubtitle,
	};


	return (
		<SectionContainer
			background={background}
			Navigation={
				<SectionNav
					{...navigationProps}
					hasPrevious={!!screen.previous}
					hasNext={currentScreen !== 'thankYou' || navigationProps.hasNext}
					canContinue={!!screen.canContinue}
					next={screen.next}
					previous={screen.previous}
					selectedLanguage={selectedLanguage}
				/>
			}
		>
			{screen && <screen.component {...screenProps} />}
		</SectionContainer>
	);
};

SafetyCertificatesCompanySection.type = 'SafetyCertificatesCompanySection';
SafetyCertificatesCompanySection.hasNavigation = true;
SafetyCertificatesCompanySection.spec = [{
	name: 'SafetyCertificatesCompanySection',
	key: 'SafetyCertificatesCompanySection',
	title: 'Section to collect company safety certificates',
	allowedFields: [],
	fields: [
		{
			type: 'TextField',
			name: 'title',
			label: 'Section title',
		},
		{
			type: 'Checkbox',
			name: 'showTypesInSubtitle',
			label: 'Show document types in subtitle?',
		},
		{
			type: 'Checkbox',
			name: 'useCamera',
			label: 'Force camera only (kiosk environments only — requires device to be registered)',
		},
	],
	defaultProperties: {
		refDataMap: {
			'companyData.companyName': 'group.name',
			'companyData.vatNumber': 'group.uniqueId',
		},
		title: 'Safety Certificates Group Section',
		apiUrl: '/api/validate/vca',
		noWelcome: false,
		showTypesInSubtitle: true,
	},
}];

export default SafetyCertificatesCompanySection;
